// src/components/NotificationsView.tsx
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Notification } from '../types';
import {
  HeartIcon,
  MessageSquareIcon,
  MegaphoneIcon,
  XIcon,
  FishMascotIcon,
  TrophyIcon,
  ShieldIcon,
  FlagIcon,
  MarketplaceIcon,
  CheckCircleIcon,
  UserIcon,
} from './icons';

type Props = {
  notifications: Notification[];
  onBack: () => void;
  onNotificationClick: (n: Notification) => void;
  onMarkAllRead?: () => void;
  onClearAll?: () => void;
  onMarkRead?: (notificationId: string) => void;
};

function formatTimeAgo(ts?: number) {
  if (!ts) return '';
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function pickActorName(n: any): string {
  return (
    n?.actor?.name ||
    n?.actorName ||
    n?.fromName ||
    n?.fromUserName ||
    n?.fromUser ||
    n?.authorName ||
    n?.username ||
    n?.displayName ||
    n?.senderName ||
    n?.sender ||
    'Someone'
  );
}

function isLikeType(type?: string) {
  return String(type || '').toLowerCase().includes('like');
}

function isReplyType(type?: string) {
  return String(type || '').toLowerCase().includes('reply');
}

function isCommentType(type?: string) {
  const t = String(type || '').toLowerCase();
  return t.includes('comment') && !t.includes('like');
}

function isFollowType(type?: string) {
  const t = String(type || '').toLowerCase();
  return t.includes('follow');
}

function isCrewMessageType(type?: string) {
  return String(type || '').toLowerCase() === 'crew_message';
}

function isCrewType(type?: string) {
  const t = String(type || '').toLowerCase();
  return t.includes('friend_request') || t.includes('friend_accepted') || t.includes('crew');
}

function isAnnouncementType(type?: string) {
  const t = String(type || '').toLowerCase();
  return t.includes('announce') || t.includes('announcement') || t === 'admin';
}

function isMarketplaceType(type?: string) {
  const t = String(type || '').toLowerCase();
  return t.includes('market') || t.includes('bazaar') || t.includes('store') || t.includes('purchase') || t.includes('sale');
}

function isTournamentType(type?: string) {
  const t = String(type || '').toLowerCase();
  return t.includes('tournament') || t.includes('winner') || t.includes('award') || t.includes('trophy');
}

function isSafetyType(type?: string) {
  const t = String(type || '').toLowerCase();
  return t.includes('safety') || t.includes('ban') || t.includes('verify') || t.includes('moder') || t.includes('flag');
}

function buildFallbackMessage(n: any): string {
  const actor = pickActorName(n);
  const type = String(n?.type || '').toLowerCase();
  const postTitle = n?.postTitle || n?.postCaption || n?.caption || n?.postText || '';
  const postHint = postTitle ? `: "${String(postTitle).slice(0, 64)}${String(postTitle).length > 64 ? '…' : ''}"` : '';

  if (isLikeType(type)) return `${actor} liked your post${postHint}`;
  if (isReplyType(type)) return `${actor} replied to your comment${postHint}`;
  if (isCommentType(type)) return `${actor} commented on your post${postHint}`;
  if (isCrewType(type)) {
    if (isCrewMessageType(type)) return `${actor} sent you a Crew message.`;
    if (type.includes('crew_post')) return `${actor} posted in one of your crews.`;
    if (type.includes('crew_reply')) return `${actor} replied in one of your crews.`;
    if (type.includes('accepted')) return `You and ${actor} are now crew.`;
    return `${actor} wants to join your crew.`;
  }
  if (isFollowType(type)) return `${actor} followed you`;
  if (isMarketplaceType(type)) return `${actor} updated something in Baleen Bay Bazaar`;
  if (isTournamentType(type)) return `Tournament update from ${actor}`;
  if (isSafetyType(type)) return 'Account / safety update';
  if (isAnnouncementType(type)) return `Announcement from ${actor}`;
  if (n?.title && !n?.message) return String(n.title);
  return `${actor} sent an update`;
}

function IconForType({ type }: { type?: any }) {
  const t = String(type || '').toLowerCase();

  if (isLikeType(t)) return <HeartIcon className="h-4 w-4 text-[#E6535F] dark:text-[#FF7A86]" />;
  if (isReplyType(t) || isCommentType(t) || isCrewMessageType(t)) return <MessageSquareIcon className="h-4 w-4" />;
  if (isCrewType(t) || isFollowType(t)) return <UserIcon className="h-4 w-4 text-[#D97706] dark:text-[#FFB45C]" />;
  if (isMarketplaceType(t)) return <MarketplaceIcon className="h-4 w-4" />;
  if (isTournamentType(t)) return <TrophyIcon className="h-4 w-4" />;
  if (isSafetyType(t)) return <ShieldIcon className="h-4 w-4" />;
  if (t.includes('flag')) return <FlagIcon className="h-4 w-4" />;
  if (isAnnouncementType(t)) return <MegaphoneIcon className="h-4 w-4" />;
  return <FishMascotIcon className="h-4 w-4" />;
}

export default function NotificationsView({
  notifications,
  onBack,
  onNotificationClick,
  onMarkAllRead,
  onClearAll,
  onMarkRead,
}: Props) {
  const { t } = useTranslation();
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const sorted = useMemo(() => {
    const list = [...notifications].sort((a: any, b: any) => (b.timestamp || 0) - (a.timestamp || 0));
    return showUnreadOnly ? list.filter((n: any) => !n.read) : list;
  }, [notifications, showUnreadOnly]);

  const unreadCount = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);
  const totalCount = notifications.length;

  return (
    <div className="flex h-[calc(100dvh-var(--roddo-header-h,72px)-var(--roddo-nav-h,72px))] min-h-0 w-full flex-col px-3 pb-3 pt-3 sm:px-5 sm:pt-4">
      <section className="relative mx-auto flex min-h-0 w-full max-w-[460px] flex-1 flex-col text-[#30271E] dark:text-[#F4F6FA]">
        

        <div className="relative flex items-center gap-3 rounded-[16px] border border-black/[.08] bg-[#FFFCF7] px-4 py-3.5 shadow-[0_6px_16px_rgba(89,65,37,.06)] dark:border-[rgba(180,195,220,.12)] dark:bg-[#121A2B] dark:shadow-none">
          <button
            type="button"
            onClick={onBack}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-[#2F80ED]/20 bg-[#2F80ED]/[.07] text-[#2F80ED] transition hover:bg-[#2F80ED]/[.11] active:translate-y-px dark:border-[#7057E8]/25 dark:bg-[#7057E8]/[.12] dark:text-[#B9ACFF]"
            aria-label={t('common.back', 'Back')}
            title={t('common.back', 'Back')}
          >
            <span className="text-xl font-black leading-none">‹</span>
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-[15px] font-black tracking-[-0.01em] text-zinc-800 dark:text-white">
                {t('notifications.title', 'Notifications')}
              </h2>
              {unreadCount > 0 && (
                <span className="rounded-full bg-[#2F80ED] px-2 py-0.5 text-[10px] font-black text-white dark:bg-[#7057E8]">
                  {unreadCount}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[11px] font-semibold text-zinc-500 dark:text-white/55">
              {unreadCount > 0
                ? t('notifications.unreadCount', '{{count}} unread', { count: unreadCount })
                : t('notifications.allCaughtUp', 'All caught up.')}
              {totalCount > 0 ? ` · ${totalCount} total` : ''}
            </p>
          </div>

          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-black/[.07] bg-[#F2EBE1] text-[#2F80ED] dark:border-white/[.08] dark:bg-[#182238] dark:text-[#B9ACFF]">
            <FishMascotIcon className="h-5 w-5" />
          </div>
        </div>

        <div className="relative pb-3 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-[12px] border border-black/[.08] bg-[#F2EBE1] p-1 dark:border-white/[.08] dark:bg-[#0D1322]">
              <button
                type="button"
                onClick={() => setShowUnreadOnly(false)}
                className={`rounded-full px-3.5 py-2 text-xs font-black transition ${
                  !showUnreadOnly
                    ? 'bg-white text-[#2F80ED] shadow-sm dark:bg-[#182238] dark:text-[#B9ACFF]'
                    : 'text-zinc-500 hover:text-zinc-800 dark:text-white/55 dark:hover:text-white'
                }`}
              >
                {t('notifications.filterAll', 'All')}
              </button>

              <button
                type="button"
                onClick={() => setShowUnreadOnly(true)}
                className={`rounded-full px-3.5 py-2 text-xs font-black transition ${
                  showUnreadOnly
                    ? 'bg-white text-[#2F80ED] shadow-sm dark:bg-[#182238] dark:text-[#B9ACFF]'
                    : 'text-zinc-500 hover:text-zinc-800 dark:text-white/55 dark:hover:text-white'
                }`}
              >
                {t('notifications.filterUnread', 'Unread')}
              </button>
            </div>

            <div className="min-w-0 flex-1" />

            {onMarkAllRead && unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAllRead}
                className="inline-flex items-center gap-1.5 rounded-[11px] border border-[#2F80ED]/25 bg-[#2F80ED] px-3.5 py-2 text-xs font-black text-white transition hover:-translate-y-0.5 active:translate-y-0 dark:border-[#7057E8]/30 dark:bg-[#7057E8]"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {t('notifications.markAllRead', 'Mark read')}
              </button>
            )}

            {onClearAll && totalCount > 0 && (
              <button
                type="button"
                onClick={onClearAll}
                className="rounded-[11px] border border-red-300/45 bg-red-50/70 px-3.5 py-2 text-xs font-black text-red-700 transition hover:-translate-y-0.5 active:translate-y-0 dark:border-red-400/20 dark:bg-red-400/[.08] dark:text-red-200"
              >
                {t('notifications.clearAll', 'Clear')}
              </button>
            )}
          </div>
        </div>

        <div className="relative min-h-0 flex-1 space-y-2 overflow-y-auto pb-3 pr-0.5 roddo-content-scroll">
          {sorted.length === 0 ? (
            <div className="rounded-[14px] border border-black/[.08] bg-[#FFFCF7] p-5 text-center dark:border-white/[.08] dark:bg-[#121A2B]">
              <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-[12px] bg-[#2F80ED]/[.08] text-[#2F80ED] dark:bg-[#7057E8]/[.12] dark:text-[#B9ACFF]">
                <FishMascotIcon className="h-6 w-6" />
              </div>
              <p className="text-sm font-black text-zinc-800 dark:text-white">
                {t('notifications.empty', 'No notifications yet.')}
              </p>
              <p className="mt-1 text-xs font-semibold text-zinc-500 dark:text-white/55">
                {t('notifications.emptyHint', 'Your Roddo updates will land here.')}
              </p>
            </div>
          ) : (
            sorted.map((n: any) => {
              const isClickable = !!n.link;
              const message = n.message && String(n.message).trim() ? String(n.message).trim() : buildFallbackMessage(n);
              const isUnread = !n.read;

              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (isUnread) onMarkRead?.(n.id);
                    if (isClickable) onNotificationClick(n);
                  }}
                  className={`group relative w-full overflow-hidden rounded-[16px] border p-3 text-left transition ${
                    isUnread
                      ? 'border-black/[.09] bg-[#FFFCF7] shadow-[0_4px_12px_rgba(89,65,37,.045)] dark:border-[rgba(180,195,220,.14)] dark:bg-[#182238] dark:shadow-none'
                      : 'border-black/[.07] bg-[#F8F3EB] opacity-90 dark:border-[rgba(180,195,220,.10)] dark:bg-[#121A2B]'
                  } ${isClickable ? 'hover:-translate-y-0.5 hover:border-black/[.13] active:translate-y-0 dark:hover:border-[rgba(180,195,220,.22)]' : 'cursor-default'}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`grid h-10 w-10 shrink-0 place-items-center rounded-[13px] border shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] ${
                        isUnread
                          ? 'border-[#DCCFBE] bg-[#F4EBDD] text-[#2F80ED] dark:border-[rgba(180,195,220,.14)] dark:bg-[#121A2B] dark:text-[#B9ACFF]'
                          : 'border-black/8 bg-white/60 text-zinc-600 dark:border-white/10 dark:bg-white/[0.055] dark:text-white/72'
                      }`}
                    >
                      <IconForType type={n.type} />
                    </div>

                    <div className="min-w-0 flex-1 pt-0.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="leading-snug text-[13px] font-black text-zinc-800 dark:text-white">
                          {message}
                        </p>

                        {isUnread && (
                          <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-[#2F80ED] dark:bg-[#7057E8]" />
                        )}
                      </div>

                      <div className="mt-1.5 flex items-center gap-2 text-[11px] font-semibold text-zinc-500 dark:text-white/52">
                        <span>{formatTimeAgo(n.timestamp) || t('notifications.justNow', 'Just now')}</span>
                        {isClickable && <span className="h-1 w-1 rounded-full bg-current opacity-40" />}
                        {isClickable && <span>{t('notifications.tapToOpen', 'Tap to open')}</span>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
